import React from 'react';
import { motion } from 'framer-motion';
import { X, Clock, Users, Star, CheckCircle, ArrowRight, Award } from 'lucide-react';
import Button from '../../../components/ui/Button';

const ComparisonTool = ({ roadmaps, onClose, onEnroll }) => {
  if (!roadmaps?.length) return null;

  const comparisonFields = [
    { key: 'difficulty', label: 'Difficulty Level', icon: <Star className="w-4 h-4" /> },
    { key: 'estimatedTime', label: 'Duration', icon: <Clock className="w-4 h-4" /> },
    { key: 'technology', label: 'Technology', icon: <CheckCircle className="w-4 h-4" /> },
    { key: 'industry', label: 'Industry Focus', icon: <Users className="w-4 h-4" /> },
    { key: 'missions', label: 'Missions Count', icon: <CheckCircle className="w-4 h-4" /> },
    { key: 'enrolled', label: 'Enrolled Learners', icon: <Users className="w-4 h-4" /> },
    { key: 'rating', label: 'Rating', icon: <Star className="w-4 h-4" /> },
    { key: 'completionRate', label: 'Success Rate', icon: <CheckCircle className="w-4 h-4" /> }
  ];

  const getDifficultyScore = (difficulty) => {
    const scores = { 'Beginner': 1, 'Intermediate': 2, 'Advanced': 3, 'Expert': 4 };
    return scores?.[difficulty] || 0;
  };

  const getDurationWeeks = (duration) => {
    const match = duration?.match(/(\d+)-?(\d+)?\s*weeks?/);
    if (match) {
      const min = parseInt(match?.[1]);
      const max = match?.[2] ? parseInt(match?.[2]) : min;
      return (min + max) / 2;
    }
    return 0;
  };

  const getBestChoice = () => {
    // Simple scoring algorithm - in real app this would be more sophisticated
    return roadmaps?.reduce((best, current) => {
      const currentScore = (current?.rating || 0) * 10 + (current?.completionRate || 0) + (current?.enrolled || 0) / 100;
      const bestScore = (best?.rating || 0) * 10 + (best?.completionRate || 0) + (best?.enrolled || 0) / 100;
      return currentScore > bestScore ? current : best;
    }, roadmaps?.[0]);
  };

  const bestChoice = getBestChoice();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-background rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="border-b border-border p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold">Compare Roadmaps</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-auto max-h-[70vh]">
          {/* Best Choice Recommendation */}
          {bestChoice && roadmaps?.length > 1 && (
            <div className="bg-green-50 dark:bg-green-900/20 border-b border-border p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Award className="w-5 h-5 text-green-600" />
                <span className="font-semibold text-green-800 dark:text-green-200">
                  Recommended Choice
                </span>
              </div>
              <p className="text-green-700 dark:text-green-300 text-sm">
                Based on rating, success rate, and popularity, <strong>{bestChoice?.title}</strong> appears to be the best option for most learners.
              </p>
            </div>
          )}

          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="text-left py-4 pr-6 font-semibold min-w-[200px]">
                      Feature
                    </th>
                    {roadmaps?.map((roadmap) => (
                      <th key={roadmap?.id} className="text-center py-4 px-4 font-semibold min-w-[200px]">
                        <div className="flex flex-col items-center space-y-2">
                          <div className={`w-12 h-12 ${roadmap?.color} rounded-lg flex items-center justify-center text-white`}>
                            {roadmap?.icon}
                          </div>
                          <div className="text-sm font-medium">{roadmap?.title}</div>
                          {roadmap?.id === bestChoice?.id && (
                            <div className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                              Recommended
                            </div>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparisonFields?.map((field, index) => (
                    <tr
                      key={field?.key}
                      className={`border-t border-border ${index % 2 === 0 ? 'bg-muted/5' : ''}`}
                    >
                      <td className="py-4 pr-6 font-medium flex items-center space-x-2">
                        {field?.icon}
                        <span>{field?.label}</span>
                      </td>
                      {roadmaps?.map((roadmap) => (
                        <td key={roadmap?.id} className="text-center py-4 px-4">
                          {field?.key === 'difficulty' && (
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                              roadmap?.difficulty === 'Beginner' ? 'bg-green-100 text-green-800' :
                              roadmap?.difficulty === 'Intermediate' ? 'bg-blue-100 text-blue-800' :
                              roadmap?.difficulty === 'Advanced'? 'bg-purple-100 text-purple-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {roadmap?.difficulty}
                            </span>
                          )}
                          {field?.key === 'enrolled' && (
                            <span className="font-semibold">
                              {roadmap?.enrolled?.toLocaleString()}
                            </span>
                          )}
                          {field?.key === 'rating' && (
                            <div className="flex items-center justify-center space-x-1">
                              <Star className="w-4 h-4 text-yellow-500" />
                              <span className="font-semibold">{roadmap?.rating}</span>
                            </div>
                          )}
                          {field?.key === 'completionRate' && (
                            <span className="font-semibold text-green-600">
                              {roadmap?.completionRate}%
                            </span>
                          )}
                          {!['difficulty', 'enrolled', 'rating', 'completionRate']?.includes(field?.key) && (
                            <span className="text-sm">
                              {roadmap?.[field?.key]}
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}

                  {/* Prerequisites Row */}
                  <tr className="border-t border-border">
                    <td className="py-4 pr-6 font-medium flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4" />
                      <span>Prerequisites</span>
                    </td>
                    {roadmaps?.map((roadmap) => (
                      <td key={roadmap?.id} className="text-center py-4 px-4">
                        <div className="space-y-1">
                          {roadmap?.prerequisites?.slice(0, 2)?.map((prereq, idx) => (
                            <div key={idx} className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded-full">
                              {prereq}
                            </div>
                          ))}
                          {roadmap?.prerequisites?.length > 2 && (
                            <div className="text-xs text-muted-foreground">
                              +{roadmap?.prerequisites?.length - 2} more
                            </div>
                          )}
                        </div>
                      </td>
                    ))}
                  </tr>

                  {/* Key Outcomes Row */}
                  <tr className="border-t border-border">
                    <td className="py-4 pr-6 font-medium flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4" />
                      <span>Key Outcomes</span>
                    </td>
                    {roadmaps?.map((roadmap) => (
                      <td key={roadmap?.id} className="text-center py-4 px-4">
                        <div className="space-y-1">
                          {roadmap?.outcomes?.slice(0, 3)?.map((outcome, idx) => (
                            <div key={idx} className="text-xs flex items-center justify-center space-x-1">
                              <CheckCircle className="w-3 h-3 text-green-500" />
                              <span>{outcome}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="border-t border-border p-6 bg-muted/5">
          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Close Comparison
            </Button>
            <div className="flex flex-col sm:flex-row gap-3">
              {roadmaps?.map((roadmap) => (
                <Button
                  key={roadmap?.id}
                  variant={roadmap?.id === bestChoice?.id ? "default" : "outline"}
                  onClick={() => {
                    onEnroll(roadmap?.slug);
                    onClose();
                  }}
                  className="flex items-center space-x-2"
                >
                  <span>
                    {roadmap?.id === bestChoice?.id ? 'Start Recommended' : `Start ${roadmap?.title?.split(' ')?.[0]}`}
                  </span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ComparisonTool;